<template lang="pug">
div
  BriqMultilanguagePickerPanel(
    v-if="hasMultilanguage"
    v-model="culture"
    :defaultCulture="defaultCulture.DisplayName"
    :cultures="availableCultures"
    :showSecondColumn="shouldShowSecondColumn"
    @toggle="toggleMultilanguage"
    :disabled="disabled"
    )
  ResourceSettingsPanel(
    :resource="resource"
    :externalTypes="externalTypes"
    :resourceTypes="resourceTypes"
    :resourceGroups="resourceGroups"
    :enableResourceGroups="enableResourceGroups"
    :vuelidate="vuelidate"
    @integrationProviderSelected="integrationProviderSelected"
    )
  Prefill(
    v-show="showExternalResourceLink"
    feedType="resource"
    :error="prefillError"
    :canAddMultipleExternalItems="canAddMultipleExternalItems"
    :options="feedResourcesWithExternalId"
    :selectedExternalIds="resource.ExternalResources"
    :display="displayResource"
    :formatExternalItem="formatResource"
    @selected="onSelectExternalResource"
    @decouple="decoupleExternalResource"
    :disabled="disabled"
    )
  BriqMultilanguageColumnsPanel(
    v-if="externalTypes"
    :showSecondColumn="shouldShowSecondColumn"
    :translationFields="translationFields"
    )
    ResourceFields(
      :resource="resource"
      ref="resourceFields"
      :hasMultilanguage="false"
      :resourceTypes="resourceTypes"
      :disabled="disabled"
      :vuelidate="vuelidate"
      )
    template(v-slot:secondary)
      ResourceFields(
        :resource="translationModel"
        :disabled="disabled"
        :hasMultilanguage="hasMultilanguage"
        :resourceTypeFallback="resource.ResourceType"
        :resourceTypes="resourceTypes"
        :vuelidate="vuelidate"
        )
  BriqPanel
    ResourceScheduleFields(
      :resource="resource"
      :resourceTypes="resourceTypes"
      :vuelidate="vuelidate"
      :isExternalResourceLinkVisible="showExternalResourceLink"
      )
  BriqPanel(v-if="showSubResourcesTable")
    SubResourcesTable(
      ref="SubResourcesTable"
      :childResources="resource.ChildResources" 
      :resources="resources"
      :resource="resource"
      :resourceKey="resource.Key"
      @toggleChildResource="linkChildResource"
      @removeNewlyAddedChildResource="unlinkChildResource"
      )
</template>

<script>
import Vue from 'vue'
import { defineComponent } from '@vue/composition-api'
import Prefill from '@v/components/Prefill.vue'
import ResourceSettingsPanel from './ResourceGeneralTab/ResourceSettingsPanel.vue'
import SubResourcesTable from './ResourceGeneralTab/SubResourcesTable.vue'
import ResourceFields from './ResourceGeneralTab/ResourceFields.vue'
import ResourceScheduleFields from './ResourceGeneralTab/ResourceScheduleFields.vue'
import { displayResource, formatResource } from '@v/components/prefillMatchFormatter.js'
import {
  getExternalDeletedItems,
  deletedExternalItemErrorFormatter,
} from '@v/util/deletedExternalItemsHelper.js'
import { canLinkExternal } from './canLinkExternal.js'
import BriqMultilanguagePickerPanel from '@v/components/BriqMultilanguagePickerPanel.vue'
import BriqMultilanguageColumnsPanel from '@v/components/BriqMultilanguageColumnsPanel.vue'
import completeTranslationInfo from '@v/util/completeTranslationInfo.js'
import BriqMultilanguageWarningModal from '@/vue/components/BriqMultilanguageWarningModal.vue'
import { propEq, reject, any, pathEq, assoc, prop, chain, map } from 'ramda'
import ResourceLinkingToExternalResource from '@v/inventory/products/edit/models/ResourceLinkingToExternalResource.js'

export default defineComponent({
  name: 'ResourceGeneralTab',
  components: {
    BriqMultilanguagePickerPanel,
    ResourceSettingsPanel,
    Prefill,
    ResourceFields,
    BriqMultilanguageColumnsPanel,
    BriqMultilanguageWarningModal,
    SubResourcesTable,
    ResourceScheduleFields,
  },
  props: {
    forEditorModel: { type: Object, required: true },
    resource: { type: Object, required: true },
    disabled: Boolean,
    enableMultilanguage: Boolean,
    enableResourceGroups: Boolean,
    resources: Array,
    feedResources: Array,
    resourceTypes: Array,
    vuelidate: { type: Object, required: true },
  },
  data() {
    return {
      formatResource,
      displayResource,
      deletedExternalResources: [],
      deletedExternalResourcesErrorMessage: null,
      culture: null,
      showSecondColumn: false,
    }
  },
  computed: {
    feedResourcesWithExternalId() {
      return map(chain(assoc('ExternalId'), prop('ExternalResourceID')))(this.feedResources)
    },
    externalTypes() {
      return this.forEditorModel.ExternalTypes
    },
    canAddMultipleExternalItems() {
      return any(
        pathEq(
          ['FeatureFlags', 'ResourceLinkingToExternalResource'],
          ResourceLinkingToExternalResource.Multiple,
        ),
      )(this.forEditorModel.ExternalTypes)
    },
    canAddExternalResources() {
      return (
        this.feedResources &&
        this.feedResources.length > 0 &&
        canLinkExternal(this.resource.ExternalType)(this.forEditorModel.ExternalTypes)
      )
    },
    vuelidatePasses() {
      return !this.$refs.resourceFields || this.$refs.resourceFields.isValid
    },
    showSubResourcesTable() {
      return (
        this.enableResourceGroups &&
        this.resource.isResourceGroup() &&
        this.resource.Key !== undefined
      )
    },
    showExternalResourceLink() {
      return this.canAddExternalResources && !this.resource.isResourceGroup()
    },
    resourceGroups() {
      return this.resources.filter(propEq('ResourceType', 'ResourceGroup'))
    },

    hasMultilanguage() {
      return this.enableMultilanguage && !!this.resource.TranslationInfo?.AvailableCultures?.length
    },
    shouldShowSecondColumn() {
      return this.hasMultilanguage && this.showSecondColumn
    },
    availableCultures() {
      return this.resource.TranslationInfo?.AvailableCultures.filter(
        c => c.Name !== this.defaultCulture.Name,
      )
    },
    defaultCulture() {
      return this.resource.TranslationInfo?.DefaultCulture
    },
    translationModel: {
      get() {
        if (!this.hasMultilanguage || !this.culture) return {}
        return this.resource.TranslationInfo.Translations[this.culture.Name]
      },
      set() {},
    },
    translationFields() {
      return ['Name']
    },
    translatableProperties() {
      return [
        {
          label: 'Name',
          key: 'Description',
        },
      ]
    },
    prefillError() {
      return this.vuelidate.resource.ExternalResources?.$error
        ? 'If you have an external resource provider selected you must select at least one external resource'
        : this.deletedExternalResourcesErrorMessage
    },
  },
  methods: {
    decoupleExternalResource(externalResourceId) {
      const externalResources = this.resource.ExternalResources
      const index = externalResources.indexOf(externalResourceId)
      if (index > -1) {
        externalResources.splice(index, 1)
      }
      this.resource.ExternalResources = externalResources
      this.vuelidate.$touch()
    },
    integrationProviderSelected(value) {
      this.resource.ExternalType = value
      this.resource.ExternalResources = []
    },
    onSelectExternalResource(suggestion) {
      const externalIdsIsArray = Array.isArray(this.resource.ExternalResources)
      const externalResourcesData = externalIdsIsArray ? this.resource.ExternalResources : []
      if (suggestion.item.ExternalResources) {
        for (let value of Object.values(suggestion.item.ExternalResources)) {
          externalResourcesData.push(value)
        }
      }
      if (!externalIdsIsArray) Vue.set(this.resource, 'ExternalResources', externalResourcesData)
      if (externalResourcesData.length === 1 && this.resource.Key === null) {
        for (let [key, value] of Object.entries(suggestion.item)) {
          if (value !== null && key !== 'ExternalType' && key !== 'ResourceType')
            this.resource[key] = value
        }
      }
    },
    toggleMultilanguage() {
      this.showSecondColumn = !this.showSecondColumn
    },
    linkChildResource(selectedResource) {
      if (selectedResource.checked) this.resource.ChildResources.push(selectedResource.resource)
      else
        this.resource.ChildResources = this.resource.ChildResources.filter(resource => {
          return selectedResource.resource.TempKey !== undefined
            ? resource.TempKTempKeyey !== selectedResource.resource.TempKey
            : resource.Key !== selectedResource.resource.Key
        })
    },
    unlinkChildResource(resource) {
      this.resource.ChildResources = reject(
        propEq('TempKey', resource.TempKey),
        this.resource.ChildResources,
      )
    },
  },
  beforeMount() {
    this.deletedExternalResources = getExternalDeletedItems({
      externalItemsIds: this.resource.ExternalResources,
      feedOptions: this.feedResources,
      itemId: 'ExternalResourceID',
    })
    this.deletedExternalResourcesErrorMessage = deletedExternalItemErrorFormatter({
      deletedExternalItems: this.deletedExternalResources,
      source: 'resources',
    })
    if (this.hasMultilanguage) {
      this.culture = this.availableCultures[0]
      completeTranslationInfo(this.resource)
    }
    if (!this.resource.ChildResources) {
      this.resource.ChildResources = []
    }
  },
})
</script>
